"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { sendResetPasswordEmail, resetPassword } from "../redux/slices/authSlice";
import { useDispatch } from "react-redux";
import { EyeClosed, EyeOff, Check, X } from "lucide-react"
import PhoneInput from "react-phone-input-2";
import { validatePassword, getPasswordChecks, getPasswordStrength } from "../utils/passwordValidation";
import CustomModal from "./Modal";

function ForgotPasswordForm() {
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const [email, setEmail] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [emailOTP, setEmailOTP] = useState("")
  const [phoneOTP, setPhoneOTP] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isOTPSubmitted, setIsOTPSubmitted] = useState(false)
  const [phone, setPhone] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isEmailSent, setIsEmailSent] = useState(false)
  const [error, setError] = useState("")
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [usePhone, setUsePhone] = useState(false)

  // Password validation logic (only used in reset password step)
  const passwordChecks = getPasswordChecks(newPassword, "");
  const passwordStrength = getPasswordStrength(newPassword, "");

  // --- OTP Countdown State ---
  const [otpTimeLeft, setOtpTimeLeft] = useState(60)
  const [otpExpired, setOtpExpired] = useState(false)
  const otpTimerRef = useRef(null)

  const startOTPTimer = () => {
    clearInterval(otpTimerRef.current)
    setOtpTimeLeft(60)
    setOtpExpired(false)
    otpTimerRef.current = setInterval(() => {
      setOtpTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(otpTimerRef.current)
          setOtpExpired(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleResendOTP = async () => {
    if (isLoading) return;
    setError("");
    setIsLoading(true);

    try {
      const result = await dispatch(sendResetPasswordEmail(usePhone ? {phone} : {email: email}));
      
      if (sendResetPasswordEmail.fulfilled.match(result)) {
        console.log("OTP resent successfully");
        startOTPTimer() // restart OTP timer
      } else if (sendResetPasswordEmail.rejected.match(result)) {
        const backendError = result.payload?.error || "Failed to resend OTP. Please try again.";
        setError(backendError);
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      setError("An error occurred while resending OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    if (otpExpired && isEmailSent && !isOTPSubmitted) {
      setError("OTP has expired. Please request a new one.")
      return
    }
    setIsLoading(true)

    try {
      if (isEmailSent && isOTPSubmitted) {
        const passwordError = validatePassword(newPassword, "");
        if (passwordError) {
          setError(passwordError);
          return;
        }

        if (newPassword !== confirmPassword) {
          setError("Passwords do not match");
          return;
        }

        const resetData = usePhone ? {
          phone: phone,
          newPassword,
          emailOTP,
          phoneOTP
        } : {
          email: email, 
          newPassword,
          emailOTP,
          phoneOTP
        };
        
        const result = await dispatch(resetPassword(resetData));
        
        if (resetPassword.fulfilled.match(result)) {
          setShowSuccessModal(true);
        } else if (resetPassword.rejected.match(result)) {
          const backendError = result.payload?.error || "Password reset failed. Please try again.";
          setError(backendError);
        }
      } else {
        const result = await dispatch(sendResetPasswordEmail(usePhone ? {phone} : {email}));
        
        if (sendResetPasswordEmail.fulfilled.match(result)) {
          setIsEmailSent(true);
          startOTPTimer() // start OTP countdown
        } else if (sendResetPasswordEmail.rejected.match(result)) {
          const backendError = result.payload?.error || "Failed to reset password. Please check your credentials.";
          setError(backendError);
        }
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    return () => clearInterval(otpTimerRef.current)
  }, [])

  return (
    <>
      <CustomModal
        isOpen={showSuccessModal}
        message="Your password has been reset successfully!"
        description={<><strong>Success!</strong> You can now login with your new password. You will be redirected to the login page.</>}
        primaryButtonText="Go to Login"
        onPrimaryClick={() => navigate("/login", { replace: true })}
        icon="🔐"
      />
      
      {isOTPSubmitted ? (
        <>
        <div className="auth-card">
          <div className="auth-header">
            <h2 className="auth-title">Reset your Password</h2>
            <p className="auth-description">Enter your new password and verify your identity.</p>
          </div>
          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="error-alert">{error}</div>}
            {/* Email/Phone field */}
            <div className="form-group m-0">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="required">{usePhone ? "Phone" : "Email"}</label>
                <div className="tabs-holder">
                  <span
                    className={`${usePhone ? '' : 'active'}`}
                    onClick={() => setUsePhone(false)}
                  >
                    Use email
                  </span>
                  <span
                    className={`${!usePhone ? '' : 'active'}`}
                    onClick={() => setUsePhone(true)}
                  >
                    Use phone
                  </span>
                </div>
              </div>
              {usePhone ? (
                <PhoneInput
                  country={"us"}
                  value={phone}
                  onChange={(value) => setPhone(value)}
                  inputClass="form-input form-control w-100"
                />
              ) : (
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="form-input form-control w-100"
                />
              )}
            </div>

            {/* New Password */}
            <div className="form-group m-0">
              <label className="required" htmlFor="newPassword">New Password</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="form-input form-control w-100"
                />
                <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeClosed size={'18'} /> : <EyeOff size={'18'} />}
                </button>
              </div>

              {/* Password Requirements */}
              <div className="password-requirements">
                <div className="requirements-header">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="requirements-title">Password must contain:</span>
                    {newPassword.length > 0 && passwordStrength && (
                      <span 
                        className="password-strength" 
                        style={{ color: passwordStrength.color, fontWeight: '500' }}
                      >
                        {passwordStrength.text}
                      </span>
                    )}
                  </div>
                </div>
                <div className="requirements-list">
                  {newPassword.length === 0 ? (
                    <>
                      <div className="requirement-item static"><span>• At least 8 characters</span></div>
                      <div className="requirement-item static"><span>• One uppercase letter (A-Z)</span></div>
                      <div className="requirement-item static"><span>• One lowercase letter (a-z)</span></div>
                      <div className="requirement-item static"><span>• One number (0-9)</span></div>
                      <div className="requirement-item static"><span>• One special character (@#$%^&*)</span></div>
                    </>
                  ) : (
                    <>
                      <div className={`requirement-item ${passwordChecks.length ? 'valid' : 'invalid'}`}>{passwordChecks.length ? <Check size={14}/> : <X size={14}/> }<span>At least 8 characters</span></div>
                      <div className={`requirement-item ${passwordChecks.uppercase ? 'valid' : 'invalid'}`}>{passwordChecks.uppercase ? <Check size={14}/> : <X size={14}/> }<span>One uppercase letter (A-Z)</span></div>
                      <div className={`requirement-item ${passwordChecks.lowercase ? 'valid' : 'invalid'}`}>{passwordChecks.lowercase ? <Check size={14}/> : <X size={14}/> }<span>One lowercase letter (a-z)</span></div>
                      <div className={`requirement-item ${passwordChecks.number ? 'valid' : 'invalid'}`}>{passwordChecks.number ? <Check size={14}/> : <X size={14}/> }<span>One number (0-9)</span></div>
                      <div className={`requirement-item ${passwordChecks.special ? 'valid' : 'invalid'}`}>{passwordChecks.special ? <Check size={14}/> : <X size={14}/> }<span>One special character (@#$%^&*)</span></div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="form-group m-0">
              <label className="required" htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-input-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="form-input form-control w-100"
                />
                <button type="button" className="password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <EyeClosed size={'18'} /> : <EyeOff size={'18'} />}
                </button>
              </div>
              {confirmPassword.length > 0 && (
                <div className={`requirement-item ${newPassword === confirmPassword ? 'valid' : 'invalid'}`} style={{ marginTop: '8px' }}>
                  {newPassword === confirmPassword ? <Check size={14} /> : <X size={14} />}
                  <span>{newPassword === confirmPassword ? 'Passwords match' : 'Passwords do not match'}</span>
                </div>
              )}
            </div>

            <div className="text-end mb-4">
              <button type="submit" className="auth-button w-100" disabled={isLoading}>
                {isLoading ? (
                  <div className="d-flex align-items-center justify-content-center">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Resetting...
                  </div>
                ) : "Reset Password"}
              </button>
            </div>
          </form>
        </div>
        <div className="auth-links mt-3">
          <div className="auth-switch">
            <button type="button" className="link-button" onClick={() => navigate("/login")}>
              Back to Sign In
            </button>
          </div>
        </div>
      </>
      ) : isEmailSent ? (
        <div className="auth-card">
          <div className="auth-header">
            <h2 className="auth-title">Verify Your Identity</h2>
            <p className="auth-description">Check your inbox at <strong>{email}</strong> to reset your password.</p>
          </div>

          <div className="auth-form">
            <form onSubmit={() => setIsOTPSubmitted(true)} className="auth-form">
              <div className="form-group m-0">
                <label>Email Code</label>
                <input
                  onChange={(e) => setEmailOTP(e.target.value)}
                  placeholder="Enter email code"
                  required
                  className="form-input form-control w-100"
                />
              </div>
              <div className="form-group m-0">
                <label>Phone Code</label>
                <input
                  onChange={(e) => setPhoneOTP(e.target.value)}
                  placeholder="Enter phone code"
                  required
                  className="form-input form-control w-100"
                />
              </div>

              {/* OTP Countdown */}
              {!otpExpired ?
              <div className="text-end text-14 mb-2">
                <span>OTP expires in {otpTimeLeft}s</span>
              </div>
                :
              <div className="text-end text-14 mb-2">
                Didn't receive the verification?{" "}
                <span className="link-button" onClick={handleResendOTP} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.6 : 1 }}>
                  {isLoading ? (
                    <span className="d-inline-flex align-items-center">
                      {/* <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> */}
                      Resending...
                    </span>
                  ) : "Resend"}
                </span>
              </div>}

              <button type="submit" className="auth-button" disabled={isLoading || otpExpired}>
                {isLoading ? (
                  <div className="d-flex align-items-center justify-content-center">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Verifying...
                  </div>
                ) : "Verify"}
              </button>
            </form>

            <div className="auth-links">
              <div className="auth-switch">
                <button type="button" className="link-button" onClick={() => navigate("/login")}>
                  Back to sign In
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
        <div className="auth-card">
          <div className="auth-header">
            <h2 className="auth-title">Reset your Password</h2>
            <p className="auth-description">Enter your verified Email Address and we will send you a password reset link.</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="error-alert">{error}</div>}

            <div className="form-group m-0">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="required">{usePhone ? "Phone" : "Email"}</label>
                <div className="tabs-holder">
                  <span
                    className={`${usePhone ? '' : 'active'}`}
                    onClick={() => setUsePhone(false)}
                  >
                    Use email
                  </span>
                  <span
                    className={`${!usePhone ? '' : 'active'}`}
                    onClick={() => setUsePhone(true)}
                  >
                    Use phone
                  </span>
                </div>
              </div>
              {usePhone ? (
                <PhoneInput
                  country={"us"}
                  value={phone}
                  required
                  onChange={(value) => setPhone(value)}
                  inputClass="form-input form-control w-100"
                />
              ) : (
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="form-input form-control w-100"
                />
              )}
            </div>

            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? (
                <div className="d-flex align-items-center justify-content-center">
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Sending...
                </div>
              ) : (
                "Verify"
              )}
            </button>

          </form>
        </div>
        <div className="auth-links mt-3">
          <div className="auth-switch">
            <button type="button" className="link-button" onClick={() => navigate("/login")}>
              Back to Sign In
            </button>
          </div>
        </div>
      </>
      )}
    </>
  )
}

export default ForgotPasswordForm
